import { Skeleton, Stack } from "@mui/material";
import {
    type GetContractsResponse,
    type GetLifeDetailV2Response,
    PensionPhaseStatus,
    ProductType,
} from "@repo/api";
import { cs, HomepageContractSidebarItemView, HomepageContractsStyled, useIsDesktop } from "@repo/ui";
import { motion } from "framer-motion";
import { type FC, type ReactNode } from "react";
import { Link } from "gatsby";

import { useAllowedChanges, useReportError } from "@repo/logic";
import { contractLink, NumberToCZK } from "../../functions";
import { useContractById, useLifeDetail, usePensionDetail } from "../../queryHooks";
import { formatAmountWithCurrencyCode } from "../../utils";
import { useFormatFufiTitle } from "../ZivotniPojisteni/FufiPage/utils";
import LifeCardDescription from "./ContractCard/LifeCardDescription";
import {
    getContractListHeading,
    getContractTypeToDescription,
    isChangeInvestmentStrategyInProgress,
} from "./Utils/contractUtils";

const personFootballIconModule = require("@repo/ui/assets/images/icons/PersonFootball.svg");
const personFootballIcon =
    typeof personFootballIconModule === "string" ? personFootballIconModule : personFootballIconModule.default;

export const DetailLink = ({ contract, link }: { contract?: GetContractsResponse; link?: string }) => {
    const isDesktop = useIsDesktop();

    if (!contract && !link) throw new Error("Contract or link must be provided");

    const targetLink = (() => {
        if (link && !contract) return link;
        if (contract) return contractLink(contract.id, contract.type);
        return link as string;
    })();

    return (
        <HomepageContractsStyled.StyledInternalButtonLink
            to={targetLink}
            size="large"
            variant="contained"
            isDesktop={isDesktop}
        >
            Detail
        </HomepageContractsStyled.StyledInternalButtonLink>
    );
};

export const AnimationDetailLinkWrapper = ({ children }: { children?: ReactNode }) => {
    return (
        <motion.div
            initial={{ zoom: 0 }}
            animate={{ zoom: 1 }}
            exit={{ zoom: 0 }}
            transition={{
                duration: 0.2,
            }}
        >
            {children}
        </motion.div>
    );
};

type ContractCardDesktopMenuViewProps = {
    title?: string;
    description?: string;
    contractNumber?: string;
    iconElement: JSX.Element;
    setContractIndex: () => void;
    isActive: boolean;
    isLoading?: boolean;
    isError?: boolean;
    ammountDisplay?: string | JSX.Element;
    data: GetContractsResponse;
};

const ContractCardDesktopMenuView: FC<ContractCardDesktopMenuViewProps> = ({
    title,
    contractNumber,
    iconElement,
    description,
    ammountDisplay,
    isActive,
    isLoading,
    isError,
    setContractIndex,
    data,
}) => {
    const subtitle =
        description || contractNumber
            ? [description, contractNumber && `(${contractNumber})`].filter((value) => Boolean(value)).join(" ")
            : <Skeleton />;

    return (
        <HomepageContractSidebarItemView
            isActive={isActive}
            isDisabled={Boolean(isError)}
            onClick={setContractIndex}
            icon={iconElement}
            heading={title ?? <Skeleton />}
            subtitle={subtitle}
            amountDisplay={ammountDisplay}
            showDetailLink={!isError && !isLoading}
            detailLinkTo={contractLink(data.id, data.type)}
            detailLabel="Detail"
            linkComponent={Link}
        />
    );
};

type ContractCardDesktopMenuProps = {
    data: GetContractsResponse;
    setContractIndex: () => void;
    contractIndex: number;
    selectedProduct: number;
    onDetailError?: () => void;
};

export const ContractCardDesktopMenu: FC<ContractCardDesktopMenuProps> = ({
    data,
    setContractIndex,
    selectedProduct,
    contractIndex,
    onDetailError,
}) => {
    const { data: contract } = useContractById({
        id: data.id,
    });

    const { data: allowedChanges, isLoading: allowedChangesLoading } = useAllowedChanges({
        id: data.id,
        options: {
            enabled: contract?.type === ProductType.Uf && Boolean(data.id),
        },
    });

    const {
        data: starpensionData,
        isLoading: starpensionDataLoading,
        isError: starpensionError,
    } = usePensionDetail({
        id: data.id,
        options: {
            enabled: data.type === ProductType.Uf || data.type === ProductType.Pf,
        },
    });

    const { isError: lifeError, isLoading: lifeLoading } = useLifeDetail({
        id: data.id,
        contractType: ProductType.Cl,
        options: {
            enabled: data.type === ProductType.Cl,
        },
    });

    const savedAmount = starpensionData?.savedAmount ?? 0;
    const contractAmount = contract?.amount ?? 0;
    const isActive = contractIndex === selectedProduct;
    const isPensionWithError =
        (data.type === ProductType.Uf || data.type === ProductType.Pf) &&
        starpensionError &&
        data.pensionPhaseStatus !== PensionPhaseStatus.Payout;
    const isLifeWithError = data.type === ProductType.Cl && lifeError;
    const hasDetailError = isPensionWithError || isLifeWithError;
    const isDetailLoading = starpensionDataLoading || lifeLoading;
    const isDisabled = hasDetailError;

    useReportError(hasDetailError, onDetailError);

    let amountDisplay: string | JSX.Element | undefined;
    if (!hasDetailError) {
        if (data.type === ProductType.Cl) {
            amountDisplay = (
                <>
                    {cs.life.global.monthlyPaymentAmmount} {formatAmountWithCurrencyCode(data.amount)}{" "}
                    <LifeCardDescription contractId={data.id} frequency />
                </>
            );
        } else if (contract?.pensionPhaseStatus === PensionPhaseStatus.Payout) {
            amountDisplay = cs.global.terminatedContract;
        } else {
            amountDisplay = (
                <>
                    {!starpensionError && getContractTypeToDescription(data.type)}
                    <TotalSavings
                        data={data}
                        starpensionDataLoading={starpensionDataLoading}
                        starpensionError={starpensionError}
                        allowedChanges={allowedChanges}
                        allowedChangesLoading={allowedChangesLoading}
                        savedAmount={savedAmount}
                        contractAmount={contractAmount}
                    />
                </>
            );
        }
    }

    let iconElement: JSX.Element;
    if (data.isChildrenContract) {
        iconElement = (
            <Stack sx={{ mr: 4, flexShrink: 0 }}>
                <img src={personFootballIcon} alt="" width={24} height={24} />
            </Stack>
        );
    } else if (data.type === ProductType.Uf || data.type === ProductType.Pf) {
        iconElement = <HomepageContractsStyled.PigIcon />;
    } else {
        iconElement = <HomepageContractsStyled.InsuranceIcon />;
    }

    return (
        <ContractCardDesktopMenuView
            data={data}
            title={getContractListHeading(data.type, data.isChildrenContract, data.childName)}
            description={data.title}
            contractNumber={data.id}
            ammountDisplay={amountDisplay}
            isActive={isActive}
            isLoading={isDetailLoading && !hasDetailError}
            isError={isDisabled}
            setContractIndex={setContractIndex}
            iconElement={iconElement}
        />
    );
};

type TotalSavingsProps = {
    readonly data: GetContractsResponse;
    readonly starpensionDataLoading: boolean;
    readonly starpensionError: boolean;
    readonly allowedChanges?: { canChangeInvestmentStrategy?: boolean | null };
    readonly allowedChangesLoading: boolean;
    readonly savedAmount: number;
    readonly contractAmount: number;
};

const TotalSavings: FC<TotalSavingsProps> = ({
    data,
    starpensionDataLoading,
    starpensionError,
    allowedChanges,
    allowedChangesLoading,
    savedAmount,
    contractAmount,
}) => {
    if (data.type === ProductType.Uf || data.type === ProductType.Pf) {
        if (starpensionDataLoading || allowedChangesLoading)
            return <HomepageContractsStyled.InlineLoadingSavingsSkeleton />;
        if (starpensionError) return null;
        return isChangeInvestmentStrategyInProgress(allowedChanges, allowedChangesLoading, data.type) ? (
            <>: {cs.global.changeInProgress}</>
        ) : (
            <>&nbsp;{formatAmountWithCurrencyCode(savedAmount)}</>
        );
    }
    return <>&nbsp;{formatAmountWithCurrencyCode(contractAmount)}</>;
};

type ContractCardDesktopMenuWithFufiDataProps = {
    data: GetContractsResponse;
    contractId: string;
    isActive: boolean;
    setContractIndex: () => void;
    onDetailError?: () => void;
};

export const ContractCardDesktopMenuWithFufiData: FC<ContractCardDesktopMenuWithFufiDataProps> = ({
    contractId,
    isActive,
    setContractIndex,
    data: contract,
    onDetailError,
}) => {
    const formatFufiTitle = useFormatFufiTitle();

    const { data, isSuccess, isLoading, isError } = useLifeDetail<GetLifeDetailV2Response>({
        id: contractId,
        proposalNumber: contract.proposalNumber,
        contractType: ProductType.Clf,
    });

    useReportError(isError, onDetailError);

    let amountDisplay: string | JSX.Element | undefined;
    if (isSuccess) {
        amountDisplay = (
            <>
                {cs.life.global.monthlyPaymentAmmount}{" "}
                {`${NumberToCZK(data.premiumClient)} ${cs.frequencies2[data.paymentFrequency]}`}
            </>
        );
    } else if (!isError) {
        amountDisplay = (
            <>
                {cs.life.global.monthlyPaymentAmmount} <Skeleton width={250} />
            </>
        );
    }

    return (
        <ContractCardDesktopMenuView
            data={contract}
            title={getContractListHeading(ProductType.Clf)}
            description={isSuccess && data.productDescription ? formatFufiTitle(data.productDescription, data.productName) : undefined}
            ammountDisplay={amountDisplay}
            contractNumber={contract.proposalNumber ?? undefined}
            isActive={isActive}
            isLoading={isLoading && !isError}
            isError={isError}
            setContractIndex={setContractIndex}
            iconElement={
                <HomepageContractsStyled.InsuranceIcon />
            }
        />
    );
};

export default ContractCardDesktopMenu;
